<template>
  <div
    class="chat-container"
    :class="{
      'is-collapsed': chatStore.isCollapsed,
      'above-battle-tray': isBattleRoute,
      'keyboard-open': keyboardOpen,
    }"
    :style="chatViewportStyle"
  >
    <div v-if="chatStore.isCollapsed" class="chat-tab" @click="chatStore.toggleChat">
      <span>Chat
        <span v-if="chatStore.unreadCount > 0" class="unread-badge">
          ({{ chatStore.unreadCount }} new)
        </span>
      </span>
      <i class="fas fa-chevron-up"></i>
    </div>

    <div v-else class="chat-window" :style="desktopWindowStyle">
      <div
        v-if="!isMobile"
        class="chat-resize-handle"
        @pointerdown.stop="startResize"
      >
        <span class="chat-resize-grip" aria-hidden="true"></span>
      </div>

      <div class="chat-header">
        <div class="chat-channel-tabs" role="tablist" aria-label="Chat channels">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            role="tab"
            :class="{
              active: chatStore.activeTab === tab.id,
              locked: tab.id === 'guild' && !isGuilded,
            }"
            :aria-selected="chatStore.activeTab === tab.id"
            :disabled="tab.id === 'guild' && !isGuilded"
            :title="tab.id === 'guild' && !isGuilded ? 'Join a guild to unlock' : tab.label"
            @click="setTab(tab.id)"
          >
            {{ tab.label }}
            <i v-if="tab.id === 'guild' && !isGuilded" class="fas fa-lock"></i>
            <span
              v-if="chatStore.unreadByTab[tab.id] > 0 && chatStore.activeTab !== tab.id"
              class="tab-unread"
            ></span>
          </button>
        </div>
        <button
          type="button"
          class="chat-collapse-btn chat-header-desktop"
          aria-label="Collapse chat"
          @click="chatStore.toggleChat"
        >
          <i class="fas fa-chevron-down"></i>
        </button>
        <button
          type="button"
          class="chat-close-btn chat-header-mobile"
          aria-label="Close chat"
          @click="chatStore.toggleChat"
        >
          ✕
        </button>
      </div>

      <div class="chat-messages" ref="messagesContainer" @scroll="handleScroll">
        <div
          v-for="msg in chatStore.visibleMessages"
          :key="msg.id"
          class="chat-message"
          :class="getMessageClass(msg)"
          :title="formatTime(msg.created_at)"
        >
          <span
            v-if="chatStore.activeTab === 'all'"
            class="channel-badge"
            :class="channelBadge(msg).className"
          >[{{ channelBadge(msg).label }}]</span>
          <template v-if="msg.type === 'system'">
            <span v-if="chatStore.activeTab !== 'all'" class="system-tag">[System]</span>
            <span class="message-text">{{ systemText(msg) }}</span>
          </template>
          <template v-else>
            <span class="timestamp" v-if="showTimestamps">[{{ formatTime(msg.created_at) }}]</span>
            <span
              class="sender-name"
              :style="{ color: getRaceColor(msg.senderRace) }"
              @click="initiateWhisper(msg.senderName)"
            >
              {{ msg.senderName }}
            </span>
            <span v-if="msg.recipientName" class="whisper-tag">
              to {{ msg.recipientName }}:
            </span>
            <span v-else class="message-colon">:</span>
            <span class="message-text">{{ msg.message }}</span>
          </template>
        </div>
      </div>

      <div class="chat-input-area">
        <form @submit.prevent="submitMessage">
          <input
            type="text"
            v-model="inputText"
            :maxlength="MAX_MESSAGE_LENGTH"
            :placeholder="inputPlaceholder"
            ref="chatInput"
          />
          <button type="submit" @pointerdown.prevent="submitMessage">
            <i class="fas fa-paper-plane"></i>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useChatStore, MAX_MESSAGE_LENGTH } from "../store/chatStore";
import { useAuthStore } from "../store/auth";
import { useGuildStore } from "../store/guild";
import { getRaceColor } from "../utils/raceColors";

const MOBILE_MAX_WIDTH = 768;
const HEIGHT_KEY = "celestia_chat_height";
const DEFAULT_HEIGHT = 320;
const MIN_HEIGHT = 240;

const tabs = [
  { id: "all", label: "ALL" },
  { id: "world", label: "WORLD" },
  { id: "personal", label: "PERSONAL" },
  { id: "guild", label: "GUILD" },
];

const route = useRoute();
const chatStore = useChatStore();
const authStore = useAuthStore();
const guildStore = useGuildStore();
const messagesContainer = ref(null);
const chatInput = ref(null);
const inputText = ref("");
const isScrolledUp = ref(false);
const showTimestamps = ref(true);
const forceScrollOnce = ref(false);
const isMobile = ref(false);
const keyboardOpen = ref(false);
const sending = ref(false);
const chatViewportHeight = ref(null);
const desktopHeight = ref(DEFAULT_HEIGHT);
const isBattleRoute = computed(() => route.name === "GameBattle");
const isGuilded = computed(() => Boolean(guildStore.membership));

const chatViewportStyle = computed(() => {
  if (!isMobile.value || chatStore.isCollapsed || chatViewportHeight.value == null) {
    return {
      height: null,
      top: null,
      bottom: null,
      "--chat-viewport-height": null,
    };
  }
  const height = chatViewportHeight.value;
  return {
    height,
    top: "0px",
    bottom: "auto",
    "--chat-viewport-height": height,
  };
});

const desktopWindowStyle = computed(() => {
  if (isMobile.value) return {};
  return { height: `${desktopHeight.value}px` };
});

let visualViewportBound = false;
let resizing = false;
let resizeStartY = 0;
let resizeStartHeight = DEFAULT_HEIGHT;

function clampDesktopHeight(value) {
  const maxH = Math.min(window.innerHeight - 120, 680);
  return Math.max(MIN_HEIGHT, Math.min(maxH, value));
}

function restoreDesktopHeight() {
  const raw = Number.parseInt(localStorage.getItem(HEIGHT_KEY) || "", 10);
  desktopHeight.value = clampDesktopHeight(
    Number.isFinite(raw) ? raw : DEFAULT_HEIGHT
  );
}

function startResize(event) {
  if (isMobile.value) return;
  event.preventDefault();
  resizing = true;
  resizeStartY = event.clientY;
  resizeStartHeight = desktopHeight.value;
  const handle = event.currentTarget;
  if (handle?.setPointerCapture && event.pointerId != null) {
    handle.setPointerCapture(event.pointerId);
  }
  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
}

function onPointerMove(event) {
  if (!resizing || isMobile.value) return;
  desktopHeight.value = clampDesktopHeight(
    resizeStartHeight + (resizeStartY - event.clientY)
  );
}

function onPointerUp() {
  if (!resizing) return;
  resizing = false;
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
  localStorage.setItem(HEIGHT_KEY, String(desktopHeight.value));
}

function updateIsMobile() {
  isMobile.value = window.innerWidth <= MOBILE_MAX_WIDTH;
}

function resetWindowScroll() {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function syncVisualViewport() {
  if (!isMobile.value || chatStore.isCollapsed) {
    keyboardOpen.value = false;
    chatViewportHeight.value = null;
    return;
  }
  const vv = window.visualViewport;
  const height = vv ? vv.height : window.innerHeight;
  chatViewportHeight.value = `${height}px`;
  keyboardOpen.value = height < window.innerHeight - 100;
  resetWindowScroll();
  if (!isScrolledUp.value) {
    nextTick(() => scrollToBottom(false));
  }
}

function bindVisualViewport() {
  if (visualViewportBound) return;
  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener("resize", syncVisualViewport);
    vv.addEventListener("scroll", syncVisualViewport);
  }
  visualViewportBound = true;
}

function unbindVisualViewport() {
  if (!visualViewportBound) return;
  const vv = window.visualViewport;
  if (vv) {
    vv.removeEventListener("resize", syncVisualViewport);
    vv.removeEventListener("scroll", syncVisualViewport);
  }
  visualViewportBound = false;
}

function syncViewportBinding() {
  const wasMobile = isMobile.value;
  updateIsMobile();
  if (!wasMobile && isMobile.value) {
    resizing = false;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }
  if (isMobile.value && !chatStore.isCollapsed) {
    bindVisualViewport();
    syncVisualViewport();
  } else {
    unbindVisualViewport();
    keyboardOpen.value = false;
    chatViewportHeight.value = null;
  }
}

function setTab(tab) {
  if (tab === "guild" && !isGuilded.value) return;
  chatStore.setActiveTab(tab);
}

function onKeydown(event) {
  if (event.key === "Escape" && !chatStore.isCollapsed) {
    chatStore.toggleChat();
  }
}

onMounted(() => {
  restoreDesktopHeight();
  syncViewportBinding();
  window.addEventListener("resize", syncViewportBinding);
  window.addEventListener("keydown", onKeydown);
  if (authStore.user) {
    chatStore.initSocket();
    guildStore.fetchMy().catch(() => {});
  }
  if (!chatStore.isCollapsed) {
    scrollToBottom(false);
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", syncViewportBinding);
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
  unbindVisualViewport();
});

watch(() => authStore.user, (newVal) => {
  if (newVal) {
    chatStore.initSocket();
    guildStore.fetchMy().catch(() => {});
  } else {
    chatStore.disconnect();
  }
});

watch(
  () => guildStore.membership?.id,
  (guildId, prevId) => {
    if (!guildId) {
      chatStore.leaveGuildTabIfNeeded();
    }
    if (guildId !== prevId && chatStore.socket) {
      chatStore.fetchHistory();
    }
  }
);

watch(() => chatStore.historyLoaded, async (loaded) => {
  if (loaded && !chatStore.isCollapsed) {
    await nextTick();
    scrollToBottom(false);
  }
});

watch(() => chatStore.messages.length, async (len, prevLen) => {
  if (chatStore.isCollapsed) return;
  if (prevLen === 0 && chatStore.historyLoaded && len > 1) {
    await nextTick();
    scrollToBottom(false);
    return;
  }
  const last = chatStore.messages[chatStore.messages.length - 1];
  const isPersonalSystem =
    last && last.type === "system" && last.recipient_id != null;
  if (!isScrolledUp.value || isPersonalSystem || forceScrollOnce.value) {
    await nextTick();
    scrollToBottom(isPersonalSystem);
    forceScrollOnce.value = false;
  }
});

watch(() => chatStore.isCollapsed, async (collapsed) => {
  syncViewportBinding();
  if (!collapsed) {
    await nextTick();
    scrollToBottom(false);
    if (chatInput.value) {
      chatInput.value.focus();
    }
  }
});

watch(() => chatStore.activeTab, async () => {
  if (chatStore.isCollapsed) return;
  await nextTick();
  scrollToBottom(false);
});

const handleScroll = () => {
  const container = messagesContainer.value;
  if (!container) return;
  const isAtBottom =
    container.scrollHeight - container.scrollTop <= container.clientHeight + 10;
  isScrolledUp.value = !isAtBottom;
};

const scrollToBottom = (smooth = true) => {
  const el = messagesContainer.value;
  if (!el) return;
  const prev = el.style.scrollBehavior;
  el.style.scrollBehavior = smooth ? "smooth" : "auto";
  el.scrollTop = el.scrollHeight;
  el.style.scrollBehavior = prev || "";
  isScrolledUp.value = false;
};

const formatTime = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const getMessageClass = (msg) => {
  return {
    "system-message": msg.type === "system",
    "whisper-message": msg.channel === "whisper" || msg.recipientName != null,
    "world-message": msg.channel === "world",
    "guild-message": msg.channel === "guild",
  };
};

function channelBadge(msg) {
  if (msg.type === "system" || msg.channel === "system") {
    return { label: "System", className: "badge-system" };
  }
  if (msg.channel === "guild") {
    return { label: "Guild", className: "badge-guild" };
  }
  if (msg.channel === "whisper") {
    return { label: "Whisper", className: "badge-whisper" };
  }
  return { label: "World", className: "badge-world" };
}

function systemText(msg) {
  return String(msg.message || "").replace(/^\[System\]\s*/i, "");
}

const inputPlaceholder = computed(() => {
  if (chatStore.activeTab === "guild") return "Message guild...";
  if (chatStore.activeTab === "personal") return "Type /w [Name] [msg]...";
  if (chatStore.activeTab === "world") return "Type a message or /w [Name]...";
  return "Type a message or /w [Name]...";
});

const initiateWhisper = (nickname) => {
  if (nickname === authStore.user?.nickname) return;
  inputText.value = `/w ${nickname} `;
  chatStore.setActiveTab("personal");
  if (chatInput.value) chatInput.value.focus();
};

const submitMessage = async () => {
  const val = inputText.value.trim();
  if (!val || sending.value) return;

  sending.value = true;
  try {
    await chatStore.sendMessage(val);
    inputText.value = "";
  } catch (error) {
    console.error("Chat error:", error);
    alert(typeof error === "string" ? error : "An error occurred.");
  } finally {
    sending.value = false;
  }
};
</script>

<style scoped>
.chat-container {
  position: fixed;
  bottom: 80px;
  left: 20px;
  z-index: 1000;
  font-family: 'Orbitron', Arial, sans-serif;
  pointer-events: none;
}

.chat-tab {
  pointer-events: auto;
  background-color: rgba(10, 10, 20, 0.85);
  border: 1px solid #00f6ff;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  padding: 8px 15px;
  color: #00f6ff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 0 10px rgba(0, 246, 255, 0.3);
  transition: all 0.2s ease;
  font-size: 14px;
}
.chat-tab:hover {
  background-color: rgba(0, 246, 255, 0.1);
  box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
}
.unread-badge {
  color: #ff00ff;
  font-weight: bold;
  animation: pulse 2s infinite;
}

.chat-window {
  pointer-events: auto;
  width: 350px;
  height: 320px;
  background-color: rgba(15, 15, 25, 0.95);
  border: 1px solid #00f6ff;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 0 20px rgba(0, 246, 255, 0.4);
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(5px);
  position: relative;
}

.chat-resize-handle {
  flex-shrink: 0;
  height: 8px;
  width: 100%;
  cursor: ns-resize;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  touch-action: none;
}

.chat-resize-grip {
  width: 28px;
  height: 2px;
  border-radius: 1px;
  background: #64748b;
  box-shadow: 0 -3px 0 #64748b, 0 3px 0 #64748b;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.chat-resize-handle:hover .chat-resize-grip {
  background: #38bdf8;
  box-shadow:
    0 -3px 0 #38bdf8,
    0 3px 0 #38bdf8,
    0 0 8px rgba(56, 189, 248, 0.8);
}

.chat-header {
  background: rgba(0, 246, 255, 0.1);
  border-bottom: 1px solid #00f6ff;
  padding: 6px 8px;
  color: #00f6ff;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.chat-header-mobile {
  display: none;
}

.chat-collapse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  color: #00f6ff;
  cursor: pointer;
}

.chat-channel-tabs {
  display: flex;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.chat-channel-tabs button {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 28px;
  padding: 4px 2px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  color: #64748b;
  font-family: "Orbitron", sans-serif;
  font-size: 9px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  cursor: pointer;
}

.chat-channel-tabs button.active {
  color: #38bdf8;
  border-bottom-color: #38bdf8;
  text-shadow: 0 0 8px rgba(56, 189, 248, 0.85);
  box-shadow: 0 4px 10px rgba(56, 189, 248, 0.25);
}

.chat-channel-tabs button.locked,
.chat-channel-tabs button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.chat-channel-tabs button .fa-lock {
  margin-left: 3px;
  font-size: 8px;
}

.tab-unread {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #38bdf8;
  box-shadow: 0 0 6px #38bdf8;
}

.channel-badge {
  font-size: 10px;
  font-weight: bold;
  margin-right: 5px;
  letter-spacing: 0.02em;
}

.badge-world { color: #38bdf8; }
.badge-guild { color: #34d399; }
.badge-whisper { color: #c084fc; }
.badge-system { color: #fbbf24; }

.chat-messages {
  flex-grow: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  scroll-behavior: smooth;
  min-height: 0;
}

.chat-message {
  word-break: break-word;
  line-height: 1.4;
}

.timestamp {
  color: #666;
  margin-right: 5px;
  font-size: 11px;
}

.sender-name {
  font-weight: bold;
  cursor: pointer;
  text-shadow: 0 0 3px currentColor;
}
.sender-name:hover {
  text-decoration: underline;
}

.message-colon {
  color: #aaa;
  margin-right: 5px;
}

.message-text {
  color: #e0e0e0;
}

.system-message .system-tag {
  color: #ff9900;
  font-weight: bold;
  margin-right: 5px;
}
.system-message .message-text {
  color: #ffcc66;
  font-style: italic;
}

.whisper-message .whisper-tag {
  color: #ff00ff;
  margin-right: 5px;
  font-style: italic;
  font-size: 0.9em;
}
.whisper-message .message-text {
  color: #ffb3ff;
}

.guild-message .message-text {
  color: #bbf7d0;
}

.chat-input-area {
  padding: 10px;
  border-top: 1px solid rgba(0, 246, 255, 0.3);
  background: rgba(0, 0, 0, 0.5);
}
.chat-input-area form {
  display: flex;
  gap: 8px;
}
.chat-input-area input {
  flex-grow: 1;
  background: transparent;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  padding: 6px 10px;
  font-family: inherit;
  font-size: 13px;
}
.chat-input-area input:focus {
  outline: none;
  border-color: #00f6ff;
  box-shadow: 0 0 5px rgba(0, 246, 255, 0.5);
}
.chat-input-area button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 246, 255, 0.2);
  border: 1px solid #00f6ff;
  color: #00f6ff;
  border-radius: 4px;
  padding: 0 12px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s;
}
.chat-input-area button:hover {
  background: #00f6ff;
  color: #000;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
}

@media (max-width: 768px) {
  .chat-resize-handle {
    display: none;
  }

  .chat-container.is-collapsed {
    left: 12px;
    bottom: calc(80px + env(safe-area-inset-bottom, 0px) + 10px);
    z-index: 1001;
  }

  .chat-container.is-collapsed.above-battle-tray {
    bottom: calc(80px + 50px + env(safe-area-inset-bottom, 0px) + 10px);
  }

  .chat-container.is-collapsed .chat-tab {
    border-bottom: 1px solid #00f6ff;
    border-radius: 8px;
    box-shadow: 0 0 14px rgba(0, 246, 255, 0.4);
  }

  .chat-container:not(.is-collapsed) {
    inset: 0;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100vw;
    height: var(--chat-viewport-height, 100dvh);
    z-index: 9999;
    pointer-events: auto;
  }

  .chat-container:not(.is-collapsed) .chat-window {
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    min-height: 0;
    overflow: hidden;
    border-radius: 0;
    border: none;
    background-color: rgba(5, 7, 15, 0.96);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow: none;
  }

  .chat-header-desktop {
    display: none;
  }

  .chat-header-mobile {
    display: inline-flex;
  }

  .chat-header {
    cursor: default;
    border-radius: 0;
    padding: 10px 12px;
    gap: 12px;
  }

  .chat-channel-tabs {
    gap: 6px;
  }

  .chat-channel-tabs button {
    min-height: 40px;
    padding: 0 6px;
    border: 1px solid rgba(0, 246, 255, 0.4);
    border-radius: 5px;
    font-size: 11px;
  }

  .chat-channel-tabs button.active {
    background: rgba(56, 189, 248, 0.2);
    border-color: #38bdf8;
    color: #38bdf8;
    box-shadow: 0 0 12px rgba(56, 189, 248, 0.35);
  }

  .chat-close-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    padding: 0;
    line-height: 1;
    background: transparent;
    border: 1px solid #00f6ff;
    border-radius: 5px;
    color: #00f6ff;
    font-size: 1.15rem;
    cursor: pointer;
  }

  .chat-close-btn:hover,
  .chat-channel-tabs button:hover:not(:disabled) {
    background-color: rgba(0, 246, 255, 0.2);
    box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
  }

  .chat-messages {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 1rem;
  }

  .chat-input-area {
    flex-shrink: 0;
    padding: 10px 12px calc(0.5rem + env(safe-area-inset-bottom, 0px));
  }

  .chat-container.keyboard-open .chat-input-area {
    padding-bottom: 0.5rem;
  }

  .chat-input-area input {
    min-height: 40px;
    font-size: 16px;
  }

  .chat-input-area button {
    min-width: 44px;
    padding: 0;
  }
}
</style>
